import { useState } from 'react';
import { X, Mail, Lock, User, Building2, ChevronDown, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const ROLES = ['Researcher', 'Clinician', 'Genetic Counselor', 'Bioinformatician', 'Student', 'Other'];

export default function AuthModal({ isOpen, onClose }) {
  const { signIn, signUp } = useAuth();
  const [view, setView]       = useState('signin'); // 'signin' | 'signup'
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [showPw, setShowPw]   = useState(false);

  const [form, setForm] = useState({
    email: '', password: '', fullName: '', institution: '', role: '',
  });

  if (!isOpen) return null;

  const set = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (view === 'signin') {
        await signIn({ email: form.email, password: form.password });
        onClose();
      } else {
        await signUp({
          email: form.email,
          password: form.password,
          fullName: form.fullName,
          institution: form.institution,
          role: form.role,
        });
        setSuccess('Account created! Check your email to confirm your address.');
      }
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const switchView = (v) => {
    setView(v);
    setError('');
    setSuccess('');
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop"
      style={{ background: 'rgba(1,15,24,0.75)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="w-full max-w-md rounded-2xl shadow-2xl overflow-hidden"
        style={{ background: '#fff' }}
      >
        {/* Header */}
        <div className="px-7 pt-7 pb-5 relative" style={{ background: '#021f30' }}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-all"
          >
            <X size={16} />
          </button>
          <h2 className="text-white font-bold text-xl">
            {view === 'signin' ? 'Welcome back' : 'Create account'}
          </h2>
          <p className="text-white/50 text-sm mt-1">
            {view === 'signin'
              ? 'Sign in to access your saved variants and history'
              : 'Join Haploix to save searches, bookmark variants, and request VCF analysis'}
          </p>

          {/* Tab switcher */}
          <div className="flex gap-1 mt-5 p-1 rounded-xl" style={{ background: 'rgba(255,255,255,0.08)' }}>
            {['signin', 'signup'].map((v) => (
              <button
                key={v}
                onClick={() => switchView(v)}
                className="flex-1 py-2 rounded-lg text-sm font-semibold transition-all"
                style={{
                  background: view === v ? '#fff' : 'transparent',
                  color: view === v ? '#021f30' : 'rgba(255,255,255,0.55)',
                }}
              >
                {v === 'signin' ? 'Sign In' : 'Register'}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="px-7 py-6">
          {success ? (
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <CheckCircle size={48} style={{ color: '#22c55e' }} />
              <p className="text-gray-700 font-medium">{success}</p>
              <button
                onClick={() => switchView('signin')}
                className="text-sm font-semibold"
                style={{ color: '#f93441' }}
              >
                Back to Sign In
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              {/* Sign-up only fields */}
              {view === 'signup' && (
                <>
                  <Field icon={<User size={15} />} placeholder="Full name"
                    value={form.fullName} onChange={set('fullName')} required />
                  <Field icon={<Building2 size={15} />} placeholder="Institution (optional)"
                    value={form.institution} onChange={set('institution')} />

                  {/* Role dropdown */}
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <ChevronDown size={15} />
                    </div>
                    <select
                      value={form.role}
                      onChange={set('role')}
                      className="w-full pl-9 pr-4 py-3 rounded-xl text-sm border outline-none appearance-none"
                      style={{ borderColor: '#e2e8f0', color: form.role ? '#021f30' : '#94a3b8', background: '#f8fafc' }}
                    >
                      <option value="">Role (optional)</option>
                      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                </>
              )}

              {/* Email */}
              <Field icon={<Mail size={15} />} placeholder="Email address" type="email"
                value={form.email} onChange={set('email')} required />

              {/* Password */}
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <Lock size={15} />
                </div>
                <input
                  type={showPw ? 'text' : 'password'}
                  placeholder="Password"
                  value={form.password}
                  onChange={set('password')}
                  required
                  minLength={8}
                  className="w-full pl-9 pr-10 py-3 rounded-xl text-sm border outline-none focus:border-blue-400 transition-all"
                  style={{ borderColor: '#e2e8f0', background: '#f8fafc', color: '#021f30' }}
                />
                <button type="button" onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>

              {error && (
                <p className="text-sm rounded-lg px-3 py-2"
                  style={{ background: '#fef2f2', color: '#dc2626' }}>
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-xl font-semibold text-white text-sm transition-all hover:brightness-110 active:scale-95 disabled:opacity-50 mt-1"
                style={{ background: '#f93441' }}
              >
                {loading ? 'Please wait…' : view === 'signin' ? 'Sign In' : 'Create Account'}
              </button>

              {view === 'signin' && (
                <p className="text-center text-xs text-gray-400">
                  Don't have an account?{' '}
                  <button type="button" onClick={() => switchView('signup')}
                    className="font-semibold" style={{ color: '#f93441' }}>
                    Register
                  </button>
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ icon, ...props }) {
  return (
    <div className="relative">
      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">{icon}</div>
      <input
        {...props}
        className="w-full pl-9 pr-4 py-3 rounded-xl text-sm border outline-none focus:border-blue-400 transition-all"
        style={{ borderColor: '#e2e8f0', background: '#f8fafc', color: '#021f30' }}
      />
    </div>
  );
}
